/**
 * 182's local force read, the first narrow piece (66.15; moved here for 66.16, whose plays read it in the Match): round one
 * place, never side-wide (the owner, 182: *"Force ratios and enemy locations probably need to be pretty localized"*). Pure.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import { teamPicture } from './picture';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecDot, vecNormalize } from '../math';
import { dacos } from './dmath';
import { PHYS_DT } from '../constants';

/** The beliefs counted: the bots' floor (TEAM_TRACK_MIN_CONFIDENCE). */
export const LOCAL_FORCE = { minConfidence: 0.25 };

export interface LocalForce {
  /** Their believed number round the place: each belief by its confidence. */
  enemies: number;
  /** Ours there: living, on the ground (and arrivals, weighted, when asked). */
  friends: number;
  /** friends / max(enemies, 0.5). */
  ratio: number;
  /** The freshest belief's age there (s), or null with none. */
  freshest: number | null;
}

/**
 * 182's local force read, scoped to 66.15's use (the owner, 182: *"Force ratios and enemy locations probably need to be pretty
 * localized"*): round one place, never side-wide — the side's beliefs within `radiusPeople` (the team picture, each by its
 * confidence, at least the bots' floor) against its living units there. Arrivals (the owner: *"Probably should be considered
 * but distance and vector modulate impact maybe?"*) are optional: each unit given counts by how near it is (1 at the radius,
 * 0 at `reachPeople` beyond) and half when it is not coming toward the place.
 */
export function localForce(state: GameState, team: number, place: { planetIndex: number; surfaceDir: Vec3 }, radiusPeople: number, tick: number,
  arrivals?: { units: readonly PlayerState[]; reachPeople: number; toward?: (u: PlayerState) => boolean }): LocalForce {
  const planet = state.planets[place.planetIndex];
  if (!planet) return { enemies: 0, friends: 0, ratio: 0, freshest: null };
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const people = (d: Vec3) => (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(d), vecNormalize(place.surfaceDir))))) * planet.radius) / person;
  let enemies = 0, freshest: number | null = null;
  for (const b of teamPicture(state, team, tick)) {
    if (b.planetIndex !== place.planetIndex || b.confidence < LOCAL_FORCE.minConfidence || people(b.surfaceDir) > radiusPeople) continue;
    enemies += b.confidence;
    const age = (tick - b.tick) * PHYS_DT;
    if (freshest === null || age < freshest) freshest = age;
  }
  let friends = 0;
  for (const u of state.players) if (u.alive && !u.airborne && u.teamIndex === team && u.planetIndex === place.planetIndex && people(u.surfaceDir) <= radiusPeople) friends++;
  if (arrivals) for (const u of arrivals.units) {
    if (!u.alive || u.planetIndex !== place.planetIndex) continue;
    const d = people(u.surfaceDir);
    if (d <= radiusPeople) continue;   // (counted above)
    const near = Math.max(0, 1 - (d - radiusPeople) / Math.max(1e-9, arrivals.reachPeople));
    friends += near * (arrivals.toward && !arrivals.toward(u) ? 0.5 : 1);
  }
  return { enemies, friends, ratio: friends / Math.max(enemies, 0.5), freshest };
}

